import React, { useEffect, useRef, useState } from 'react';
import { Navigate, Outlet } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { adminLogout, adminLoginSuccess } from '../../store/adminSlice';
import { showToast } from '../../store/uiSlice';
import { adminApi } from '../../services/api';

const SESSION_DURATION = 30 * 60 * 1000; // 30 minutes

const AdminRoute = () => {
  const dispatch = useDispatch();
  const { isAdminAuthenticated, adminToken, adminUser } = useSelector((state) => state.admin);
  const sessionTimerRef = useRef(null);
  const activityTimerRef = useRef(null);

  // While we're re-verifying the token with the server, don't render children
  const [isVerifying, setIsVerifying] = useState(true);

  // Determine auth from Redux (persisted) OR localStorage fallback
  const token = adminToken || localStorage.getItem('adminToken');
  const isAuthed = isAdminAuthenticated || !!token;

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminUser');
    localStorage.removeItem('adminLoginTime');
    dispatch(adminLogout());
    dispatch(showToast({ message: 'Admin session expired. Please login again.', type: 'error' }));
  };

  // ── On mount: always pull a fresh admin object from the server ──────────
  // This ensures permission changes made in the DB (via the Staff Management
  // UI or directly in SQL) take effect on the next page refresh, without
  // needing to log out and log back in.
  useEffect(() => {
    let cancelled = false;

    const hydrate = async () => {
      if (!token) {
        setIsVerifying(false);
        return;
      }

      try {
        const res = await adminApi.get('/admin/auth/verify');
        if (cancelled) return;

        if (res.data?.valid && res.data?.admin) {
          const freshAdmin = res.data.admin;

          // Redux: replace the whole admin object, which also updates
          // adminPermissions via the adminLoginSuccess reducer.
          dispatch(adminLoginSuccess({ token, admin: freshAdmin }));

          // Refresh the localStorage snapshot so subsequent mounts without
          // a network call still have the latest data.
          localStorage.setItem('adminUser', JSON.stringify(freshAdmin));
        } else {
          handleLogout();
        }
      } catch (e) {
        if (cancelled) return;

        // 401 → interceptor already logged us out; just bail.
        // Any other error (network, 500) → fall back to the cached snapshot
        // so the user isn't kicked out by a transient outage.
        if (e?.response?.status !== 401 && !adminUser) {
          const storedUser = localStorage.getItem('adminUser');
          if (storedUser) {
            try {
              const parsed = JSON.parse(storedUser);
              dispatch(adminLoginSuccess({ token, admin: parsed }));
            } catch (err) {
              handleLogout();
            }
          }
        }
      } finally {
        if (!cancelled) setIsVerifying(false);
      }
    };

    hydrate();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Session timeout logic ─────────────────────────────────────────────
  const resetSessionTimer = () => {
    localStorage.setItem('adminLoginTime', Date.now().toString());
    if (sessionTimerRef.current) clearTimeout(sessionTimerRef.current);
    sessionTimerRef.current = setTimeout(() => {
      handleLogout();
    }, SESSION_DURATION);
  };

  useEffect(() => {
    if (!isAuthed) return;

    const loginTime = localStorage.getItem('adminLoginTime');
    if (loginTime) {
      const elapsed = Date.now() - parseInt(loginTime);
      if (elapsed > SESSION_DURATION) {
        handleLogout();
        return;
      }
      const remaining = SESSION_DURATION - elapsed;
      sessionTimerRef.current = setTimeout(() => {
        handleLogout();
      }, remaining);
    } else {
      localStorage.setItem('adminLoginTime', Date.now().toString());
      sessionTimerRef.current = setTimeout(() => {
        handleLogout();
      }, SESSION_DURATION);
    }

    const onActivity = () => {
      if (activityTimerRef.current) return;
      activityTimerRef.current = setTimeout(() => {
        activityTimerRef.current = null;
        resetSessionTimer();
      }, 60000);
    };

    window.addEventListener('click', onActivity);
    window.addEventListener('keydown', onActivity);
    window.addEventListener('mousemove', onActivity);

    return () => {
      if (sessionTimerRef.current) clearTimeout(sessionTimerRef.current);
      if (activityTimerRef.current) clearTimeout(activityTimerRef.current);
      window.removeEventListener('click', onActivity);
      window.removeEventListener('keydown', onActivity);
      window.removeEventListener('mousemove', onActivity);
    };
  }, [isAuthed]);

  if (!isAuthed) {
    return <Navigate to="/admin/login" replace />;
  }

  // Block the first render until we've refreshed the admin object from the
  // server. This ensures AdminDashboard sees the correct permissions on its
  // very first render, instead of briefly showing an empty sidebar.
  if (isVerifying) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 border-[3px] border-[#1e3a5f] border-t-transparent rounded-full animate-spin" />
          <p className="text-[11px] font-mono font-extrabold text-[#64748b] uppercase tracking-wider">
            Loading admin session…
          </p>
        </div>
      </div>
    );
  }

  return <Outlet />;
};

export default AdminRoute;